import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ScanFace, Camera, Activity, ListChecks, Settings2, RefreshCw, Plus, Trash2,
  KeyRound, Copy, PlayCircle, CheckCircle2, XCircle, AlertTriangle, Clock3, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const todayISO = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const STATUS_TONE = {
  present: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  late: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  matched: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  duplicate: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
  unknown: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
  review: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  low_confidence: "bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
};
const Pill = ({ v, testId }) => (
  <span data-testid={testId} className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_TONE[v] || "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>{(v || "").replace(/_/g, " ")}</span>
);

/* ─────────── Live View (near-live camera snapshots) ─────────── */
function LiveViewPanel({ cameras }) {
  const [frames, setFrames] = useState({}); // camera_id -> {url, at}
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    let alive = true;
    const urls = {};
    const tick = async () => {
      for (const c of cameras) {
        try {
          const res = await api.get(`/admin/face-attendance/cameras/${c.camera_id}/snapshot`, { responseType: "blob" });
          if (!alive) return;
          if (urls[c.camera_id]) URL.revokeObjectURL(urls[c.camera_id]);
          urls[c.camera_id] = URL.createObjectURL(res.data);
          setFrames((f) => ({ ...f, [c.camera_id]: { url: urls[c.camera_id], at: res.headers["x-updated-at"] || "" } }));
        } catch { /* no snapshot yet for this camera */ }
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(t); Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); };
  }, [cameras]);

  const ageSec = (iso) => (iso ? Math.max(Math.round((Date.now() - new Date(iso).getTime()) / 1000), 0) : null);
  const age = (iso) => {
    const s = ageSec(iso);
    if (s === null) return "";
    return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)}m ago` : `${Math.round(s / 3600)}h ago`;
  };

  if (cameras.length === 0) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fa-live-view">
      {cameras.map((c) => {
        const f = frames[c.camera_id];
        const stale = f && ageSec(f.at) > 60;
        return (
          <div key={c.camera_id} className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-black/90" data-testid={`fa-live-view-${c.camera_id}`}>
            <div className="aspect-video relative">
              {f ? (
                <img src={f.url} alt={c.name} className={`w-full h-full object-cover ${stale ? "opacity-40 grayscale" : ""}`} data-testid={`fa-live-frame-${c.camera_id}`} />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-xs gap-1">
                  <Camera className="h-6 w-6 opacity-40" />
                  <span>No frames yet — starts when the school edge agent runs</span>
                </div>
              )}
              {stale && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center px-4" data-testid={`fa-live-offline-${c.camera_id}`}>
                  <AlertTriangle className="h-6 w-6 text-amber-400" />
                  <div className="text-amber-300 text-xs font-semibold">Camera feed stopped {age(f.at)}</div>
                  <div className="text-[10px] text-slate-300">Check the school PC: is the edge agent window running? (start.bat)</div>
                </div>
              )}
              <div className="absolute top-2 left-2 flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur px-2.5 py-1 text-[11px] text-white">
                <span className={`h-1.5 w-1.5 rounded-full ${f && !stale ? "bg-red-500 animate-pulse" : "bg-slate-400"}`} />
                {c.name}
              </div>
              {f && <div className={`absolute bottom-2 right-2 rounded-full px-2 py-0.5 text-[10px] ${stale ? "bg-amber-500/80 text-black font-semibold" : "bg-black/60 text-slate-200"}`}>{age(f.at)}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}


const STAT_TONES = {
  slate: "rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/30 px-4 py-3",
  emerald: "rounded-2xl border border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20 px-4 py-3",
  amber: "rounded-2xl border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20 px-4 py-3",
  rose: "rounded-2xl border border-rose-200/60 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 px-4 py-3",
};
const STAT_TEXT = {
  slate: "text-2xl font-bold text-slate-700 dark:text-slate-300",
  emerald: "text-2xl font-bold text-emerald-700 dark:text-emerald-300",
  amber: "text-2xl font-bold text-amber-700 dark:text-amber-300",
  rose: "text-2xl font-bold text-rose-700 dark:text-rose-300",
};

/* ─────────── Live tab ─────────── */
function LiveTab({ readonly }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [simBusy, setSimBusy] = useState(false);
  const load = useCallback(async (retry = true) => {
    try {
      const { data } = await api.get("/admin/face-attendance/live");
      setData(data); setError("");
    } catch (e) {
      if (retry) { setTimeout(() => load(false), 1200); return; }
      setError(apiErr(e.response?.data?.detail) || "Failed to load live data");
    }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const simulate = async () => {
    setSimBusy(true);
    try {
      const { data } = await api.post("/admin/face-attendance/simulate", {});
      toast.success(`Simulated: ${data.attendance_status}${data.student_name ? ` — ${data.student_name}` : ""}`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Simulation failed"); }
    finally { setSimBusy(false); }
  };

  const s = data?.summary || {};
  if (error) return (
    <div className="py-12 text-center space-y-3" data-testid="fa-live-error">
      <div className="text-sm text-rose-500">{error}</div>
      <Button variant="outline" className="rounded-full" onClick={() => load()}>Retry</Button>
    </div>
  );
  if (!data) return <div className="py-12 text-center text-slate-400 text-sm" data-testid="fa-live-loading">Loading live data…</div>;
  return (
    <div className="space-y-5" data-testid="fa-live">
      {(data.cameras || []).some((c) => c.health?.agent_version && c.health.agent_version !== data.edge_agent_version) && (
        <div role="alert" className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-900 dark:text-amber-200" data-testid="fa-edge-update-required">
          School PC update required · Edge Agent {data.edge_agent_version}. Current camera versions: {(data.cameras || []).filter((c) => c.health?.agent_version).map((c) => `${c.name}: ${c.health.agent_version}`).join(" · ")}
        </div>
      )}
      {data.ai_enabled === false && (
        <div className="rounded-xl border border-rose-300/60 bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-300 px-4 py-2.5 text-sm font-medium" data-testid="fa-disabled-banner">
          AI Attendance is turned OFF — camera check-ins are being ignored. Enable it in Settings.
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-400">Auto-refreshes every 15s · {data?.date}</div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 rounded-full" onClick={load} data-testid="fa-live-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          {!readonly && <Button className="h-9 rounded-full bg-violet-600 hover:bg-violet-700" onClick={simulate} disabled={simBusy} data-testid="fa-simulate-btn"><PlayCircle className="h-4 w-4 mr-1" /> Simulate Check-in</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["Total Students", s.total_students, "slate"], ["Recognized", s.recognized, "emerald"], ["Present", s.present, "emerald"],
          ["Late", s.late, "amber"], ["Not Yet Detected", s.not_detected, "slate"], ["Review / Unknown", (s.manual_review || 0) + (s.unknown_faces || 0), "rose"]].map(([l, v, tone]) => (
          <div key={l} className={STAT_TONES[tone]} data-testid={`fa-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{l}</div>
            <div className={STAT_TEXT[tone]} style={{ fontFamily: "Outfit" }}>{v ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Near-live camera view (frames pushed by the school edge agent) */}
      <LiveViewPanel cameras={data?.cameras || []} />

      {/* Camera status */}
      <div className="flex flex-wrap gap-2" data-testid="fa-camera-strip">
        {(data?.cameras || []).map((c) => (
          <span key={c.camera_id} data-testid={`fa-camera-status-${c.camera_id}`} className="inline-flex flex-wrap items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs">
            <span className={`h-2 w-2 rounded-full ${c.computed_status === "ONLINE" ? "bg-emerald-500" : c.computed_status === "EVENTS_OK" ? "bg-sky-500" : "bg-slate-300"}`} />
            <b>{c.name}</b> <span className="text-slate-400">{c.location || c.entrance}</span>
            <span className="text-slate-400">· {c.computed_status === "ONLINE" ? "Online" : c.computed_status === "EVENTS_OK" ? "Receiving events" : "No signal yet"}</span>
          </span>
        ))}
        {(data?.cameras || []).length === 0 && <span className="text-xs text-slate-400">No cameras configured yet — add one in the Cameras tab.</span>}
      </div>

      {/* Events feed */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm" data-testid="fa-events-table">
          <thead className="bg-slate-50 dark:bg-slate-900/60"><tr className="text-left">
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300">Time</th>
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300">Person</th>
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300 hidden sm:table-cell">Class</th>
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300 hidden md:table-cell">Camera</th>
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300 text-center">Match</th>
            <th className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300 text-right">Result</th>
          </tr></thead>
          <tbody>
            {(data?.events || []).length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No recognition events today yet.</td></tr>}
            {(data?.events || []).map((e) => (
              <tr key={e.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2.5 text-xs text-slate-500">{(e.recognition_time || "").slice(11, 19)}</td>
                <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-100">{e.student_name || e.person_name || "Unknown"}</td>
                <td className="px-4 py-2.5 hidden sm:table-cell text-xs text-slate-500">{e.class_name ? `${e.class_name}-${e.section}` : "—"}</td>
                <td className="px-4 py-2.5 hidden md:table-cell text-xs text-slate-500">{e.camera_name || e.camera_id}</td>
                <td className="px-4 py-2.5 text-center text-xs" data-testid={`fa-event-score-${e.id}`}>{e.diagnostics?.policy_version ? `Cosine ${Number(e.diagnostics.best_score).toFixed(3)}` : e.confidence ? `${Math.round(e.confidence * 100)}%` : "—"}</td>
                <td className="px-4 py-2.5 text-right">
                  <Pill v={e.result?.attendance_status?.toLowerCase() || e.status} testId={`fa-event-result-${e.id}`} />
                  {e.result?.detail && <div className="mt-1 max-w-64 text-xs text-slate-500" data-testid={`fa-event-detail-${e.id}`}>{e.result.detail}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────── Register tab ─────────── */
function RegisterTab() {
  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/face-attendance/records", { params: { date } }); setRows(data.rows || []); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="fa-register">
      <div className="flex items-end gap-3">
        <div><Label className="text-[10px] uppercase tracking-widest text-slate-400">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-10 w-40 mt-1" data-testid="fa-register-date" /></div>
        <Button variant="outline" className="h-10" onClick={load}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
        <div className="text-xs text-slate-400 pb-2.5">{rows.length} record{rows.length === 1 ? "" : "s"} · parallel AI register (does not replace teacher attendance)</div>
      </div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm" data-testid="fa-register-table">
          <thead className="bg-slate-50 dark:bg-slate-900/60"><tr className="text-left">
            {["Student", "Class", "Session", "Status", "Entry", "Last Seen", "Exit", "Camera", "Source"].map((h) => <th key={h} className="px-4 py-2.5 font-semibold text-slate-600 dark:text-slate-300">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No AI attendance for this date.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`fa-record-${r.student_code || r.student_id}`}>
                <td className="px-4 py-2.5"><div className="font-medium text-slate-800 dark:text-slate-100">{r.student_name}</div><div className="text-[10px] text-slate-400">{r.student_code}</div></td>
                <td className="px-4 py-2.5 text-xs">{r.class_name}-{r.section}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase font-semibold text-slate-500">{r.session === "afternoon" ? "PM" : "AM"}</td>
                <td className="px-4 py-2.5"><Pill v={r.status} /></td>
                <td className="px-4 py-2.5 text-xs">{r.entry_time}</td>
                <td className="px-4 py-2.5 text-xs">{r.last_seen}</td>
                <td className="px-4 py-2.5 text-xs">{r.exit_time || "—"}</td>
                <td className="px-4 py-2.5 text-xs">{r.camera_name || r.camera_id}</td>
                <td className="px-4 py-2.5 text-[10px] text-slate-400">{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────── Review tab ─────────── */
function ReviewTab({ readonly }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [target, setTarget] = useState(null); // review item being assigned
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/face-attendance/review"); setRows(data.rows || []); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const search = async (v) => {
    setQ(v);
    if (v.trim().length < 2) { setFound([]); return; }
    try { const { data } = await api.get("/admin/face-attendance/students-search", { params: { q: v } }); setFound(data.students || []); } catch { /* noop */ }
  };
  const resolve = async (item, action, student_id = "") => {
    try {
      const { data } = await api.post(`/admin/face-attendance/review/${item.id}/resolve`, { action, student_id });
      toast.success(action === "reject" ? "Rejected" : `Confirmed — ${data.attendance_status || "done"}`);
      setTarget(null); setQ(""); setFound([]);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="fa-review">
      {rows.length === 0 && <div className="py-14 text-center text-slate-400 text-sm">🎉 Nothing pending review.</div>}
      {rows.map((it) => (
        <div key={it.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex flex-wrap items-center gap-4" data-testid={`fa-review-${it.id}`}>
          {it.snapshot_b64 ? (
            <img
              src={it.snapshot_b64.startsWith("data:") ? it.snapshot_b64 : `data:image/jpeg;base64,${it.snapshot_b64}`}
              alt="face crop"
              className="h-16 w-16 rounded-xl object-cover border border-slate-200 dark:border-slate-700 bg-slate-100 shrink-0"
              data-testid={`fa-review-face-${it.id}`}
            />
          ) : (
            <div className="h-16 w-16 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center text-[10px] text-slate-400 shrink-0" data-testid={`fa-review-no-face-${it.id}`}>
              No photo
            </div>
          )}
          <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0" />
          <div className="flex-1 min-w-52">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 capitalize">{(it.category || "").replace(/_/g, " ")}</div>
            <div className="text-xs text-slate-500">
              {[(it.recognition_time || "").slice(0, 19).replace("T", " ") || "—", it.camera_id || "—"].join(" · ")}
              {it.person_ref ? ` · device ref: ${it.person_ref}` : ""}{it.confidence ? ` · ${Math.round(it.confidence * 100)}%` : ""}
            </div>
            {it.diagnostics?.best_score != null && <div className="text-xs text-slate-500 mt-1" data-testid={`fa-review-evidence-${it.id}`}>
              Cosine {Number(it.diagnostics.best_score).toFixed(3)} · Margin {Number(it.diagnostics.margin).toFixed(3)} · {it.diagnostics.face_size_px}px · {it.diagnostics.confirmations} confirmations
            </div>}
            {(it.candidates || []).length > 0 && (
              <div className="text-xs text-slate-500 mt-1">Candidate: <b>{it.candidates[0].name}</b> ({it.candidates[0].class_name}-{it.candidates[0].section})</div>
            )}
          </div>
          {!readonly && (
            <div className="flex gap-2">
              {(it.candidates || []).length > 0 && (
                <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => resolve(it, "confirm", it.candidates[0].student_id)} data-testid={`fa-confirm-${it.id}`}>
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm {it.candidates[0].name?.split(" ")[0]}
                </Button>
              )}
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTarget(it)} data-testid={`fa-assign-${it.id}`}>Assign student…</Button>
              <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-200" onClick={() => resolve(it, "reject")} data-testid={`fa-reject-${it.id}`}>
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>
      ))}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="fa-assign-dialog">
          <DialogHeader><DialogTitle>Assign student</DialogTitle></DialogHeader>
          <Input value={q} onChange={(e) => search(e.target.value)} placeholder="Search name or student ID…" data-testid="fa-assign-search" />
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {found.map((s) => (
              <button key={s.id} className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm" onClick={() => resolve(target, "confirm", s.id)} data-testid={`fa-assign-pick-${s.student_id}`}>
                <b>{s.name}</b> <span className="text-slate-400 text-xs">· {s.class_name}-{s.section} · {s.student_id}</span>
              </button>
            ))}
            {q.length >= 2 && found.length === 0 && <div className="text-xs text-slate-400 px-2 py-3">No matches.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────── Cameras tab ─────────── */
const emptyCam = () => ({ name: "", model: "CP PLUS", ip: "", port: 80, username: "", password: "", rtsp_url: "", location: "", entrance: "", channel: "", direction: "ENTRY", recognition_mode: "AUTO", recognition_enabled: true, attendance_enabled: true });

function CamerasTab({ readonly }) {
  const [cams, setCams] = useState([]);
  const [agentVersion, setAgentVersion] = useState("");
  const [form, setForm] = useState(null); // null | {..} (new) | {..with camera_id} (edit)
  const [keyShow, setKeyShow] = useState(null); // {camera_id, device_key}
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/face-attendance/cameras");
      setCams(data.cameras || []);
      if (data.edge_agent_version) setAgentVersion(data.edge_agent_version);
    }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Camera name required");
    try {
      if (form.camera_id) { await api.put(`/admin/face-attendance/cameras/${form.camera_id}`, form); toast.success("Camera updated"); }
      else {
        const { data } = await api.post("/admin/face-attendance/cameras", form);
        setKeyShow({ camera_id: data.camera.camera_id, device_key: data.device_key });
        toast.success("Camera added — copy its device key");
      }
      setForm(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this camera? Its device key and webhook URL will stop working.")) return;
    try { await api.delete(`/admin/face-attendance/cameras/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };
  const showKey = async (id) => {
    try { const { data } = await api.get(`/admin/face-attendance/cameras/${id}/key`); setKeyShow({ camera_id: id, device_key: data.device_key }); } catch { toast.error("Failed"); }
  };
  const toggleCam = async (c) => {
    const enabled = c.attendance_enabled === false;
    try {
      await api.post(`/admin/face-attendance/cameras/${c.camera_id}/toggle`, { enabled });
      toast.success(enabled ? `${c.name} turned ON` : `${c.name} turned OFF — check-ins ignored`);
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
  };
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };
  const downloadAgent = async () => {
    try {
      const res = await api.get("/admin/face-attendance/edge-agent.zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      // Version comes from the server header so a backend bump lands in the
      // filename without a frontend redeploy.
      const ver = res.headers?.["x-edge-agent-version"] || agentVersion || "";
      const a = document.createElement("a");
      a.href = url; a.download = `zerolabs-edge-agent${ver ? "-" + ver : ""}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const webhookUrl = (id, key) => `${API}/api/webhooks/cpplus/${id}?key=${key}`;

  return (
    <div className="space-y-4" data-testid="fa-cameras">
      <div className="flex flex-wrap gap-2 items-center">
        {!readonly && <Button className="rounded-full" onClick={() => setForm(emptyCam())} data-testid="fa-add-camera"><Plus className="h-4 w-4 mr-1" /> Add Camera</Button>}
        <Button variant="outline" className="rounded-full" onClick={downloadAgent} data-testid="fa-download-agent">
          <Download className="h-4 w-4 mr-1" /> Download Edge Agent{agentVersion ? ` ${agentVersion}` : ""}
        </Button>
        <span className="text-[11px] text-slate-400">Run the edge agent on a school PC (same network as the cameras) — it streams frames & does local AI recognition.</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {cams.map((c) => (
          <div key={c.camera_id} className={`rounded-2xl border p-4 space-y-2 ${c.attendance_enabled === false ? "border-rose-200 dark:border-rose-900/40 opacity-80" : "border-slate-200 dark:border-slate-800"}`} data-testid={`fa-camera-${c.camera_id}`}>
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${c.attendance_enabled === false ? "bg-rose-400" : c.computed_status === "ONLINE" ? "bg-emerald-500" : c.computed_status === "EVENTS_OK" ? "bg-sky-500" : "bg-slate-300"}`} />
              <div className="font-semibold text-slate-800 dark:text-slate-100">{c.name}</div>
              <span className="text-[10px] rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-500">{c.direction}</span>
              <span className="text-[10px] rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-slate-500">{c.recognition_mode}</span>
              {!readonly && (
                <label className="ml-auto flex items-center gap-1.5 text-xs font-semibold select-none">
                  <Switch checked={c.attendance_enabled !== false} onCheckedChange={() => toggleCam(c)} data-testid={`fa-cam-toggle-${c.camera_id}`} />
                  <span className={c.attendance_enabled === false ? "text-rose-500" : "text-emerald-600"}>{c.attendance_enabled === false ? "OFF" : "ON"}</span>
                </label>
              )}
            </div>
            {c.attendance_enabled === false && (
              <div className="text-[11px] text-rose-500 font-medium" data-testid={`fa-cam-off-note-${c.camera_id}`}>Camera OFF — all check-ins from this camera are ignored. Live View frames still show.</div>
            )}
            <div className="text-xs text-slate-500">{c.model} · {c.location || "—"} {c.entrance ? `· ${c.entrance}` : ""} {c.ip ? `· ${c.ip}:${c.port}` : ""}</div>
            <div className="text-[11px] text-slate-400">Camera ID: <code>{c.camera_id}</code> · Last event: {c.last_event_at ? c.last_event_at.slice(0, 19).replace("T", " ") : "never"}</div>
            <div className="text-xs text-slate-500" data-testid={`fa-camera-version-${c.camera_id}`}>Edge version: {c.health?.agent_version || "Not reported"}</div>
            {!readonly && (
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...emptyCam(), ...c, password: "" })} data-testid={`fa-edit-${c.camera_id}`}>Edit</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => showKey(c.camera_id)} data-testid={`fa-key-${c.camera_id}`}><KeyRound className="h-3.5 w-3.5 mr-1" /> Device Key</Button>
                <Button size="sm" variant="outline" className="rounded-full text-rose-600 border-rose-200" aria-label="Delete camera" title="Delete camera" onClick={() => del(c.camera_id)} data-testid={`fa-del-${c.camera_id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
        ))}
        {cams.length === 0 && <div className="text-sm text-slate-400 py-8">No cameras yet. Add your CP PLUS NVR/camera to get its webhook URL & device key.</div>}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-xl" data-testid="fa-camera-dialog">
          <DialogHeader><DialogTitle>{form?.camera_id ? "Edit camera" : "Add camera"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><Label>Camera name *</Label><Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Main Gate Camera" data-testid="fa-cam-name" /></div>
              <div><Label>Location</Label><Input className="mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Main Entrance" data-testid="fa-cam-location" /></div>
              <div><Label>Entrance</Label><Input className="mt-1" value={form.entrance} onChange={(e) => setForm({ ...form, entrance: e.target.value })} placeholder="Gate 1" /></div>
              <div><Label>IP address</Label><Input className="mt-1" value={form.ip} onChange={(e) => setForm({ ...form, ip: e.target.value })} placeholder="192.168.1.108" data-testid="fa-cam-ip" /></div>
              <div><Label>Port</Label><Input className="mt-1" type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 80 })} /></div>
              <div><Label>Username</Label><Input className="mt-1" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="admin" /></div>
              <div><Label>Password {form.camera_id && <span className="text-[10px] text-slate-400">(blank = unchanged)</span>}</Label><Input className="mt-1" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="fa-cam-password" /></div>
              <div className="sm:col-span-2"><Label>RTSP URL <span className="text-[10px] text-slate-400">(for the local edge agent)</span></Label><Input className="mt-1" value={form.rtsp_url} onChange={(e) => setForm({ ...form, rtsp_url: e.target.value })} placeholder="rtsp://user:pass@192.168.1.108:554/cam/realmonitor?channel=1&subtype=0" /></div>
              <div><Label>NVR Channel</Label><Input className="mt-1" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} placeholder="CH01" /></div>
              <div>
                <Label>Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger className="mt-1" data-testid="fa-cam-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>{["ENTRY", "EXIT", "BIDIRECTIONAL"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Recognition mode</Label>
                <Select value={form.recognition_mode} onValueChange={(v) => setForm({ ...form, recognition_mode: v })}>
                  <SelectTrigger className="mt-1" data-testid="fa-cam-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CPPLUS_NATIVE">CP PLUS Native AI</SelectItem>
                    <SelectItem value="LOCAL_AI">ZeroLabs Local AI (edge agent)</SelectItem>
                    <SelectItem value="AUTO">Auto Detect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 sm:col-span-2">
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.attendance_enabled} onCheckedChange={(v) => setForm({ ...form, attendance_enabled: v })} /> Attendance enabled</label>
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.recognition_enabled} onCheckedChange={(v) => setForm({ ...form, recognition_enabled: v })} /> Recognition enabled</label>
              </div>
              <div className="sm:col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
                <Button onClick={save} data-testid="fa-cam-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Device key dialog */}
      <Dialog open={!!keyShow} onOpenChange={(o) => !o && setKeyShow(null)}>
        <DialogContent data-testid="fa-key-dialog">
          <DialogHeader><DialogTitle>Device credentials — {keyShow?.camera_id}</DialogTitle></DialogHeader>
          {keyShow && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">Device key (for edge agent header X-Device-Key)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={keyShow.device_key} data-testid="fa-device-key" />
                  <Button variant="outline" size="icon" onClick={() => copy(keyShow.device_key)}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">NVR Alarm-Center / HTTP push URL (Mode A)</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={webhookUrl(keyShow.camera_id, keyShow.device_key)} className="text-xs" data-testid="fa-webhook-url" />
                  <Button variant="outline" size="icon" onClick={() => copy(webhookUrl(keyShow.camera_id, keyShow.device_key))}><Copy className="h-4 w-4" /></Button>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Paste this URL into the NVR's HTTP event/alarm push settings. Keep the key secret.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────── Settings tab ─────────── */
function SettingsTab({ readonly }) {
  const [r, setR] = useState(null);
  const [runs, setRuns] = useState([]);
  const [sending, setSending] = useState(false);
  const loadRuns = () => api.get("/admin/face-attendance/absent-alerts").then(({ data }) => setRuns(data.rows || [])).catch(() => {});
  useEffect(() => {
    api.get("/admin/face-attendance/rules").then(({ data }) => setR(data)).catch(() => toast.error("Failed to load rules"));
    loadRuns();
  }, []);
  const save = async () => {
    try { const { data } = await api.put("/admin/face-attendance/rules", r); setR(data); toast.success("Rules saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/face-attendance/absent-alerts/send", { force: true });
      toast.success(data.started ? "Sending in background — run log updates below" : "Already ran today");
      setTimeout(loadRuns, 4000);
      setTimeout(loadRuns, 15000);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Failed"); }
    finally { setSending(false); }
  };
  if (!r) return <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>;
  const T = (k, label) => (
    <div><Label>{label}</Label><Input type="time" className="mt-1 w-36" value={r[k]} onChange={(e) => setR({ ...r, [k]: e.target.value })} disabled={readonly} data-testid={`fa-rule-${k}`} /></div>
  );
  return (
    <div className="space-y-5 max-w-2xl" data-testid="fa-settings">
      {/* Master switch */}
      <div className={`rounded-2xl border p-4 flex items-center gap-4 ${r.ai_enabled ? "border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/20" : "border-rose-200 bg-rose-50/40 dark:border-rose-900/40 dark:bg-rose-950/20"}`}>
        <Switch checked={r.ai_enabled} onCheckedChange={(v) => setR({ ...r, ai_enabled: v })} disabled={readonly} data-testid="fa-rule-ai-enabled" />
        <div>
          <div className="font-semibold text-slate-800 dark:text-slate-100">AI Attendance {r.ai_enabled ? "ON" : "OFF"}</div>
          <div className="text-xs text-slate-500">Master switch — when OFF, camera check-ins are ignored and nothing is marked. (Admin, Super Admin & Utility Admin can change this.)</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {T("attendance_start", "Attendance start")}{T("school_start", "School start")}{T("late_after", "Late after")}{T("attendance_close", "Attendance close")}
      </div>
      {/* Afternoon session */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-3" data-testid="fa-afternoon-box">
        <div className="flex items-center gap-3">
          <Switch checked={!!r.afternoon_enabled} onCheckedChange={(v) => setR({ ...r, afternoon_enabled: v })} disabled={readonly} data-testid="fa-rule-afternoon-enabled" />
          <div>
            <div className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Afternoon Session</div>
            <div className="text-xs text-slate-500">Second attendance window (e.g. after lunch). Each student gets a separate morning & afternoon record.</div>
          </div>
        </div>
        {r.afternoon_enabled && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {T("pm_attendance_start", "PM start")}{T("pm_late_after", "PM late after")}{T("pm_attendance_close", "PM close")}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 max-w-md">
        <div><Label>Native / legacy confidence ≥</Label><Input type="number" step="0.01" min="0" max="1" className="mt-1 w-28" value={r.min_confidence} onChange={(e) => setR({ ...r, min_confidence: Number(e.target.value) })} disabled={readonly} data-testid="fa-rule-min-confidence" /></div>
        <div><Label>Review floor ≥</Label><Input type="number" step="0.01" min="0" max="1" className="mt-1 w-28" value={r.review_confidence} onChange={(e) => setR({ ...r, review_confidence: Number(e.target.value) })} disabled={readonly} /></div>
      </div>
      <div className="text-xs text-slate-500" data-testid="fa-local-ai-policy">Local AI v2 · Cosine ≥ 0.45 · Margin ≥ 0.08 · 2 frames; faces below 100px: cosine ≥ 0.48 and 3 frames. Fast confirmation: ≥ 100px, cosine ≥ 0.52, margin ≥ 0.16.</div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={r.exit_tracking} onCheckedChange={(v) => setR({ ...r, exit_tracking: v })} disabled={readonly} data-testid="fa-rule-exit-tracking" /> Track exit times (EXIT cameras update exit, never overwrite Present)</label>

      {/* Absent alerts */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-3" data-testid="fa-absent-alerts-box">
        <div className="flex items-center gap-3">
          <Switch checked={!!r.absent_alerts_enabled} onCheckedChange={(v) => setR({ ...r, absent_alerts_enabled: v })} disabled={readonly} data-testid="fa-rule-absent-alerts" />
          <div>
            <div className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Absent Alerts to Parents (WhatsApp)</div>
            <div className="text-xs text-slate-500">At attendance close ({r.attendance_close}), parents of students NOT detected by the camera get a WhatsApp via AiSensy. Requires an approved campaign named <code>absent_alert</code> in your AiSensy dashboard.</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" className="rounded-full" onClick={sendNow} disabled={sending || readonly} data-testid="fa-absent-send-now">{sending ? "Sending…" : "Send absent alerts now"}</Button>
          <span className="text-[11px] text-slate-400">Manual run for today (re-runs allowed).</span>
        </div>
        {runs.length > 0 && (
          <div className="text-xs space-y-1" data-testid="fa-absent-runs">
            {runs.slice(0, 5).map((run) => (
              <div key={run.date} className="flex items-center gap-2 text-slate-500">
                <span className="font-semibold text-slate-700 dark:text-slate-300">{run.date}</span>
                <span>· {run.absent_count ?? "?"} absent · {run.sent ?? 0} sent · {run.failed ?? 0} failed · {run.no_phone ?? 0} no phone · by {run.by}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-400">Between review floor and auto-mark threshold → goes to the Review queue. Below review floor → treated as unreliable/unknown. Thresholds should be calibrated after real-world testing at your gate.</p>
      {!readonly && <Button onClick={save} data-testid="fa-rules-save">Save Rules</Button>}
    </div>
  );
}

/* ─────────── Page ─────────── */
export default function AdminFaceAttendance() {
  const [tab, setTab] = useState("live");
  const readonly = false; // super admin granted write via server whitelist
  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400 mb-2"><ScanFace className="h-4 w-4" /> AI Attendance</div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Face Recognition Attendance</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">CP PLUS camera check-ins → AI register (runs parallel to teacher attendance). Cameras capture, AI identifies, ZeroLabs decides.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="fa-tabs">
          <TabsTrigger value="live" data-testid="fa-tab-live" className="gap-1.5"><Activity className="h-4 w-4" /> Live</TabsTrigger>
          <TabsTrigger value="register" data-testid="fa-tab-register" className="gap-1.5"><Clock3 className="h-4 w-4" /> AI Register</TabsTrigger>
          <TabsTrigger value="review" data-testid="fa-tab-review" className="gap-1.5"><ListChecks className="h-4 w-4" /> Review Queue</TabsTrigger>
          <TabsTrigger value="cameras" data-testid="fa-tab-cameras" className="gap-1.5"><Camera className="h-4 w-4" /> Cameras</TabsTrigger>
          <TabsTrigger value="settings" data-testid="fa-tab-settings" className="gap-1.5"><Settings2 className="h-4 w-4" /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="live" className="mt-6"><LiveTab readonly={readonly} /></TabsContent>
        <TabsContent value="register" className="mt-6"><RegisterTab /></TabsContent>
        <TabsContent value="review" className="mt-6"><ReviewTab readonly={readonly} /></TabsContent>
        <TabsContent value="cameras" className="mt-6"><CamerasTab readonly={readonly} /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab readonly={readonly} /></TabsContent>
      </Tabs>
    </Layout>
  );
}
